<script setup lang="ts">
    const props = defineProps<{
        currentCountry: CountryEntryStoryblok;
        previousCountry: CountryEntryStoryblok | null;
    }>();

    const emit = defineEmits<{
        close: [];
        confirm: [];
    }>();

    const languagePickerModalController = getOrCreateModalController(HEADER_LANGUAGE_PICKER_MODAL_ID);

    const openLanguagePicker = () => {
        emit('close');
        languagePickerModalController.open();
    };
</script>

<template>
    <div class="section-switch-container">
        <div class="info-icon-container">
            <Icon
                name="lucide:info"
                mode="svg"
                size="32px"
                class="info-icon"
            />
        </div>

        <div class="switch-section">
            <div>
                <div class="country-name-flag">
                    <img
                        v-if="props.currentCountry.countryId !== 'global' && props.currentCountry.flagIcon?.filename"
                        :src="props.currentCountry.flagIcon.filename"
                        alt=""
                        class="flag-icon"
                    />
                    <GlobeIcon
                        v-else-if="props.currentCountry.countryId === 'global'"
                        class="flag-icon"
                    />
                    <p class="country-name">
                        {{
                            $te(`languagePicker.locations.${props.currentCountry.countryId}`)
                                ? $t(`languagePicker.locations.${props.currentCountry.countryId}`)
                                : props.currentCountry.displayName
                        }}
                    </p>
                </div>
                <SharedCloseButton
                    :label="$t('general.close')"
                    :icon-inverted="true"
                    class="close-button"
                    @click="emit('close')"
                />
            </div>

            <div class="text">
                <h2>
                    {{ props.currentCountry.title }}
                </h2>
                <RichText :doc="props.currentCountry.sectionSwitchToast" />
            </div>

            <div class="actions">
                <button
                    type="button"
                    class="toast-top-button"
                    @click="emit('close')"
                >
                    {{ props.currentCountry.primaryButton }}

                    <ChevronRightIcon />
                </button>
                <button
                    v-if="props.previousCountry"
                    type="button"
                    class="toast-bottom-button"
                    @click="emit('confirm')"
                >
                    <span class="icon-button">
                        <img
                            v-if="
                                props.previousCountry.countryId !== 'global' && props.previousCountry.flagIcon?.filename
                            "
                            :src="props.previousCountry.flagIcon.filename"
                            alt=""
                            class="flag-icon-button"
                        />
                        <GlobeIcon
                            v-else-if="props.previousCountry.countryId === 'global'"
                            class="flag-icon-button"
                        />
                        <span>
                            {{ props.currentCountry.secondaryButton }}
                            {{
                                $te(`languagePicker.locations.${props.previousCountry.countryId}`)
                                    ? $t(`languagePicker.locations.${props.previousCountry.countryId}`)
                                    : props.previousCountry.displayName
                            }}
                        </span>
                    </span>

                    <img
                        src="~/assets/icons/chevron-right-black.svg"
                        class="icon"
                        alt=""
                    />
                </button>
                <button
                    v-else
                    type="button"
                    class="toast-bottom-button"
                    @click="openLanguagePicker"
                >
                    <span class="icon-button">
                        <GlobeIcon class="flag-icon-button" />

                        <span>
                            {{ props.currentCountry.chooseDifferentCountry }}
                        </span>
                    </span>

                    <img
                        src="~/assets/icons/chevron-right-black.svg"
                        class="icon"
                        alt=""
                    />
                </button>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .section-switch-container {
        display: grid;
        grid-template-columns: 45px 1fr 45px;
        width: 100%;
    }

    .info-icon-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;

        padding-top: var(--spacing);

        background: var(--brand-primary);

        .info-icon {
            color: var(--white);
        }
    }

    .switch-section {
        padding: var(--spacing);
    }

    .country-name-flag {
        display: flex;
        align-items: center;
        gap: var(--spacing--05);

        .country-name {
            color: var(--brand-primary);
        }
        .flag-icon {
            color: var(--brand-primary);
            height: 30px;
        }
    }

    .text {
        margin-top: var(--spacing);
        display: flex;
        flex-direction: column;
        gap: var(--spacing);

        h2 {
            font-family: 'Figtree', sans-serif;
            font-size: var(--font-m);
            font-weight: 700;
        }
    }

    .actions {
        display: flex;
        flex-direction: column;
        gap: var(--spacing--05);
        margin-top: var(--spacing);
    }

    .toast-top-button,
    .toast-bottom-button {
        padding: var(--spacing--05) var(--spacing);
        border-radius: var(--border-radius);
        cursor: pointer;
        display: flex;
        align-items: center;
        font-size: medium;
        justify-content: space-between;
    }

    .toast-top-button {
        border: none;
        background: var(--brand-primary);
        color: var(--white);
    }

    .toast-bottom-button {
        border: 1px solid var(--black);
        background: var(--white);
        color: var(--brand-primary);
        .flag-icon-button {
            height: 20px;
            color: var(--brand-primary);
        }
    }
    .icon-button {
        display: flex;
        gap: var(--spacing--05);
        align-items: center;
    }
    .close-button {
        position: absolute;
        z-index: 2;
        right: var(--spacing);
        top: var(--spacing--05);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        padding: var(--spacing--05);
        background-color: var(--header-mobile-button-color, $black);
        border-radius: 100%;
        cursor: pointer;
        width: 40px;
        height: 40px;

        transition:
            background-color $anim-duration-hover $anim-ease,
            transform $anim-duration-hover $anim-ease-bounce;

        &:hover {
            background-color: var(--color-black-hover);
            transform: $anim-scale-hover;
        }
    }
</style>
<style lang="scss">
    .iziToast.switch-section-notification {
        width: 635px;
        max-width: calc(100vw - 32px);
        position: relative;

        padding: 0 !important;

        background: var(--white) !important;

        border-radius: var(--border-radius) !important;
        overflow: hidden;

        box-shadow:
            0 4px 12px rgb(0 0 0 / 10%),
            0 12px 32px rgb(0 0 0 / 12%);

        .iziToast-body {
            margin: 0 !important;
            padding: 0 !important;
        }

        .iziToast-texts {
            margin: 0 !important;
            padding: 0;
            width: 100%;
        }

        .iziToast-message {
            margin: 0 !important;
            width: 100%;
        }

        .language-switch-toast {
            width: 100%;
        }

        .section-switch-toast-progress {
            position: absolute;
            right: 0;
            bottom: 0;
            left: 0;
            height: 4px;
            background-color: var(--brand-primary);
            transform: scaleX(var(--toast-progress, 1));
            transform-origin: left;
            pointer-events: none;
        }
    }
</style>
